import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 25;

/**
 * Sorts an array by country (primary, nulls last) then by a name field (secondary, alphabetically).
 */
export function sortByCountryThenName(items, countryKey = 'country', nameKey = 'name') {
  return [...items].sort((a, b) => {
    const cA = (a[countryKey] || '').trim();
    const cB = (b[countryKey] || '').trim();
    // Items with country come first, then sort country alphabetically
    if (cA && !cB) return -1;
    if (!cA && cB) return 1;
    if (cA && cB) {
      const cc = cA.localeCompare(cB);
      if (cc !== 0) return cc;
    }
    // Within same country (or both empty), sort by name
    const nA = (typeof nameKey === 'function' ? nameKey(a) : (a[nameKey] || '')).toLowerCase();
    const nB = (typeof nameKey === 'function' ? nameKey(b) : (b[nameKey] || '')).toLowerCase();
    return nA.localeCompare(nB);
  });
}

/**
 * Returns a paginated slice of items.
 */
export function paginate(items, page, pageSize = PAGE_SIZE) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

/**
 * Pagination controls component.
 */
export function PaginationControls({ currentPage, totalItems, pageSize = PAGE_SIZE, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
      .reduce((acc, p, i, arr) => {
        if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
        acc.push(p);
        return acc;
      }, []);
  }, [totalPages, currentPage]);

  if (totalItems <= pageSize) return null;

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700" data-testid="pagination-controls">
      <span className="text-sm text-gray-400">
        Showing {start}–{end} of {totalItems}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition"
          data-testid="pagination-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {pageNumbers.map((p, i) =>
            p === '...' ? (
              <span key={`dot-${i}`} className="px-1 text-gray-500">...</span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`w-8 h-8 rounded text-sm font-medium transition ${
                  p === currentPage
                    ? 'bg-green-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                data-testid={`pagination-page-${p}`}
              >
                {p}
              </button>
            )
          )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition"
          data-testid="pagination-next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
